import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { SubscriptionApi } from 'modules/subscription/api';
import { PLAN_HIERARCHY } from 'modules/subscription';
import { Plan, PlanFeatures, PricingPlan } from 'modules/subscription/types';
import logo from 'modules/ui/assets/images/logo.png';
import { Button } from 'modules/ui/components';
import { Routes } from 'Routes';
import { AuthImageContainer } from '../../../auth/components';
import {
  PageContainer,
  LogoImage,
  Title,
  Subtitle,
  ToggleContainer,
  ToggleLabel,
  CardsContainer,
  PlanCard,
  PlanHeader,
  PlanName,
  SelectedBadge,
  PlanPrice,
  PriceAmount,
  PricePeriod,
  YearlySavings,
  FeaturesList,
  FeatureItem,
  CheckIcon,
  CardError,
  TogglePill,
  SaveBadge,
  CurrentBadge,
} from './styledComponents';
import { useGetUser } from 'modules/user';

type PlanType = 'pro' | 'unlimited';
type BillingCycle = 'monthly' | 'yearly';

type PriceEntry = { price: number; currency: string };
type PricingByType = Record<
  PlanType,
  Record<BillingCycle, PriceEntry | undefined>
>;

const PLAN_ORDER: Plan[] = [Plan.Free, Plan.Pro, Plan.Unlimited];

const PLAN_NAME: Record<Plan, string> = {
  [Plan.Free]: 'Free',
  [Plan.Pro]: 'Pro',
  [Plan.Unlimited]: 'Unlimited',
};

const CURRENCY_SYMBOL: Record<string, string> = { eur: '€', usd: '$' };

// The backend encodes "unlimited" quotas as Number.MAX_SAFE_INTEGER.
const UNLIMITED = Number.MAX_SAFE_INTEGER;

const formatCount = (count: number): string =>
  count >= UNLIMITED ? 'Unlimited' : String(count);

const buildFeatureList = (features: PlanFeatures): string[] => {
  const { meteo, routing } = features;
  const items: string[] = [];

  items.push(
    `${formatCount(features.boats)} boat${features.boats === 1 ? '' : 's'}`,
  );
  items.push(
    `${formatCount(
      routing.deterministicRoutingsPerDay,
    )} deterministic routings/day`,
  );
  if (routing.models.ensemble && routing.ensembleRoutingsPerDay > 0) {
    items.push(
      `${formatCount(routing.ensembleRoutingsPerDay)} ensemble routings/day`,
    );
  }
  if (features.liveData) items.push('Live boat data');
  if (routing.polarSpread) items.push('Polar spread analysis');
  if (routing.delayedStart) items.push('Delayed start');
  if (meteo.windComparison) items.push('Wind model comparison');
  if (meteo.ecmwf || meteo.gfs) items.push('ECMWF & GFS weather models');
  if (meteo.gfsWave) items.push('GFS wave data');
  if (meteo.nemo) items.push('NEMO ocean currents');
  if (routing.csv) items.push('CSV export');

  return items;
};

const toPricingByType = (plans: PricingPlan[]): PricingByType =>
  plans.reduce<PricingByType>(
    (acc, p) => {
      acc[p.type][p.interval] = { price: p.price, currency: p.currency };
      return acc;
    },
    {
      pro: { monthly: undefined, yearly: undefined },
      unlimited: { monthly: undefined, yearly: undefined },
    },
  );

const isPlanDisabled = (plan: Plan, currentPlan: string): boolean =>
  PLAN_HIERARCHY[plan] < PLAN_HIERARCHY[currentPlan];

const UpgradeForm: React.FC = () => {
  const history = useHistory();
  const [selectedPlan, setSelectedPlan] = useState<Plan | null>(null);
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const [pricing, setPricing] = useState<PricingByType | null>(null);
  const [features, setFeatures] = useState<Record<Plan, PlanFeatures> | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const user = useGetUser();

  const currentPlan = user.user?.subscription;

  useEffect(() => {
    SubscriptionApi.getPricing().then((response) => {
      setPricing(toPricingByType(response.plans));
      setFeatures(response.features);
    });
  }, []);

  const handleToggle = useCallback(
    (checked: boolean) => setBillingCycle(checked ? 'yearly' : 'monthly'),
    [],
  );

  const handleSubmit = useCallback(async () => {
    if (!selectedPlan) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const { url } = await SubscriptionApi.checkout({
        plan: selectedPlan,
        period: billingCycle,
      });

      window.open(url, '_blank');
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [selectedPlan, billingCycle]);

  const isYearly = billingCycle === 'yearly';
  const isKeepingCurrent =
    selectedPlan !== null && selectedPlan === currentPlan;
  const primaryLabel =
    isKeepingCurrent && selectedPlan
      ? `Keep ${PLAN_NAME[selectedPlan]}`
      : 'Subscribe';

  const handlePrimaryAction = useCallback((): void => {
    if (isKeepingCurrent) {
      history.push(Routes.App);
      return;
    }
    handleSubmit();
  }, [isKeepingCurrent, history, handleSubmit]);

  return (
    <AuthImageContainer contentWidth="820px">
      <PageContainer>
        <LogoImage src={logo} alt="Dorado sail" />
        <Title>Choose your plan</Title>
        <Subtitle>
          Upgrade to unlock advanced routing and weather features.
        </Subtitle>

        <ToggleContainer>
          <ToggleLabel active={!isYearly}>Monthly</ToggleLabel>
          <label style={{ cursor: 'pointer', display: 'flex' }}>
            <input
              type="checkbox"
              checked={isYearly}
              onChange={(e): void => handleToggle(e.target.checked)}
              style={{ display: 'none' }}
            />
            <TogglePill active={isYearly} />
          </label>
          <ToggleLabel active={isYearly}>
            Yearly <SaveBadge>Save ~17%</SaveBadge>
          </ToggleLabel>
        </ToggleContainer>

        <CardsContainer>
          {PLAN_ORDER.map((planKey) => {
            const isFree = planKey === Plan.Free;
            const isCurrent = planKey === currentPlan;
            const isDisabled = isPlanDisabled(
              planKey,
              currentPlan ?? Plan.Free,
            );
            const isSelected = selectedPlan === planKey;
            const priceEntry = isFree
              ? undefined
              : pricing?.[planKey as PlanType][billingCycle];
            const monthlyEntry = isFree
              ? undefined
              : pricing?.[planKey as PlanType]['monthly'];
            const planFeatures = features?.[planKey];
            const featureList = planFeatures
              ? buildFeatureList(planFeatures)
              : [];
            const currencySymbol =
              CURRENCY_SYMBOL[priceEntry?.currency ?? ''] ??
              priceEntry?.currency ??
              '';

            return (
              <PlanCard
                key={planKey}
                selected={isSelected}
                disabled={isDisabled}
                onClick={(): void => {
                  if (!isDisabled) {
                    setSelectedPlan(planKey);
                  }
                }}
              >
                <PlanHeader>
                  <PlanName>{PLAN_NAME[planKey]}</PlanName>
                  {isCurrent && <CurrentBadge>Current</CurrentBadge>}
                  {!isCurrent && isSelected && (
                    <SelectedBadge>Selected</SelectedBadge>
                  )}
                </PlanHeader>
                <PlanPrice>
                  {isFree ? (
                    <PriceAmount>Free</PriceAmount>
                  ) : (
                    <>
                      <PriceAmount>
                        {priceEntry
                          ? `${currencySymbol}${priceEntry.price}`
                          : '—'}
                      </PriceAmount>
                      <PricePeriod>/{isYearly ? 'year' : 'month'}</PricePeriod>
                      {isYearly && priceEntry && monthlyEntry && (
                        <YearlySavings>
                          {currencySymbol}
                          {Math.round(
                            (monthlyEntry.price * 12 - priceEntry.price) / 12,
                          )}
                          /mo savings
                        </YearlySavings>
                      )}
                    </>
                  )}
                </PlanPrice>
                <FeaturesList>
                  {featureList.map((feature) => (
                    <FeatureItem key={feature}>
                      <CheckIcon>&#10003;</CheckIcon>
                      {feature}
                    </FeatureItem>
                  ))}
                </FeaturesList>
              </PlanCard>
            );
          })}
        </CardsContainer>

        {error && <CardError>{error}</CardError>}

        <Button
          label={primaryLabel}
          clickHandler={handlePrimaryAction}
          isLoading={loading}
          disabled={!selectedPlan}
          style={{ width: 380 }}
        />
        <Button
          label="Cancel"
          clickHandler={(): void => history.push(Routes.App)}
          isSecondary
          style={{ width: 380, marginTop: 12 }}
        />
      </PageContainer>
    </AuthImageContainer>
  );
};

export default React.memo(UpgradeForm);
